<!-- Loader -->
<div style="height: 3px; width: 100%;">
  <mat-progress-bar *ngIf="isLoading || isLoadingCustomer || isLoadingVerification || isLoadingActivityLog" mode="indeterminate" color="primary"></mat-progress-bar>
</div>

<!-- Full Page Loader -->
<div *ngIf="isLoadingCustomer || isLoadingVerification || isLoadingActivityLog" class="full-page-loader">
  <div class="loader-content">
    <div class="spinner"></div>
    <p class="loader-text">Loading verification data...</p>
  </div>
</div>

<!-- Content -->
<div class="verification-container" *ngIf="!(isLoadingCustomer || isLoadingVerification || isLoadingActivityLog)">

  <!-- Status Bar -->
  <div class="status-bar">
    <div class="status-items">
      <div class="status-item" *ngIf="customerName">
        <mat-icon color="primary" class="status-icon">person</mat-icon>
        <span class="status-label">{{customerName}}</span>
      </div>

      <div class="status-item" *ngIf="customerEmail">
        <mat-icon color="primary" class="status-icon">email</mat-icon>
        <span class="status-label">{{customerEmail}}</span>
      </div>

      <div class="status-item" *ngIf="customerPhone">
        <mat-icon color="primary" class="status-icon">phone</mat-icon>
        <span class="status-label">{{customerPhone}}</span>
      </div>
    </div>

    <div class="header-actions">
      <button mat-raised-button color="accent" (click)="sendVerificationLink()" [disabled]="isSendingVerificationLink" class="action-btn send-btn">
        <mat-icon *ngIf="!isSendingVerificationLink">send</mat-icon>
        <mat-icon *ngIf="isSendingVerificationLink" class="spinner-icon">hourglass_empty</mat-icon>
        <span *ngIf="!isSendingVerificationLink">Send Verification Link</span>
        <span *ngIf="isSendingVerificationLink">Sending...</span>
      </button>
      
      <button mat-raised-button color="primary" (click)="goBack()" class="action-btn back-btn">
        <mat-icon>arrow_back</mat-icon>
        Back
      </button>
    </div>
  </div>

  <!-- Additional Notes Section - Below Status Bar -->
  <div *ngIf="additionalNotes && additionalNotes.trim().length > 0" class="notes-card-top">
    <div class="notes-card-header">
      <mat-icon class="notes-card-icon">sticky_note_2</mat-icon>
      <h3 class="notes-card-title">Additional Notes</h3>
    </div>
    <div class="notes-card-content">
      <p class="notes-card-text">{{ additionalNotes }}</p>
    </div>
  </div>

  <!-- Main Content Grid -->
  <div class="content-grid">
    
    <!-- Documents Section -->
    <div class="documents-panel">
      <div class="panel-header">
        <div class="panel-title">
          <mat-icon class="panel-icon">assignment</mat-icon>
          <h2>Document Verification</h2>
          <span class="document-count">{{documents.length}} Documents</span>
        </div>
        
        <div class="panel-controls" *ngIf="documents.length > 0">
          <mat-checkbox 
            [checked]="selectAll" 
            (change)="toggleSelectAll()"
            class="select-all-checkbox">
            Select All
          </mat-checkbox>
          <span class="selection-count">{{getSelectedCount()}} of {{documents.length}} selected</span>
        </div>
      </div>

      <!-- Action Bar -->
      <div class="action-bar" *ngIf="getSelectedCount() > 0">
        <button mat-raised-button 
                color="primary" 
                (click)="approveSelected()"
                [disabled]="!hasSelectableDocuments()"
                class="approve-btn">
          <mat-icon>check_circle</mat-icon>
          Approve {{getSelectedCount()}} Document{{getSelectedCount() > 1 ? 's' : ''}}
        </button>
      </div>

      <!-- Documents Grid -->
      <div class="documents-grid">
        <!-- Loading State -->
        <div *ngIf="isLoadingVerification" class="loading-container">
          <div class="loading-spinner">⏳</div>
          <p class="loading-text">Loading documents...</p>
        </div>

        <!-- Empty State -->
        <div *ngIf="!isLoadingVerification && documents.length === 0" class="empty-documents-state">
          <mat-icon class="empty-icon">assignment</mat-icon>
          <p class="empty-message">No documents found</p>
          <p class="empty-submessage">Documents will appear here once they are submitted</p>
        </div>

        <div *ngFor="let document of documents" 
             class="document-card" 
             [ngClass]="{
               'verified': document.verified, 
               'pending': !document.verified,
               'selected': document.selected,
               'required': document.required
             }">
          
          <div class="card-content">
            <mat-checkbox 
              [checked]="document.selected" 
              (change)="onDocumentSelect(document, $event)"
              [disabled]="document.verified"
              class="document-checkbox">
            </mat-checkbox>
            <h3 class="document-title">{{document.name}}</h3>
            <div *ngIf="document.verified" class="verified-badge">
              <mat-icon class="verified-icon">check_circle</mat-icon>
              <span>Verified</span>
            </div>
          </div>
          
          <!-- Files Section -->
          <div *ngIf="document.uploadedFiles && document.uploadedFiles.length > 0" class="files-section">
            <div class="files-header">
              <mat-icon class="files-icon">attach_file</mat-icon>
              <span class="files-count">{{document.uploadedFiles.length}} file{{document.uploadedFiles.length > 1 ? 's' : ''}}</span>
            </div>
            <div class="files-list">
              <div *ngFor="let file of document.uploadedFiles" class="file-chip">
                <mat-icon class="file-icon">description</mat-icon>
                <a 
                  *ngIf="file.url" 
                  [href]="file.url" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="file-name file-link"
                  title="Click to view file">
                  {{file.name}}
                </a>
                <span *ngIf="!file.url" class="file-name">{{file.name}}</span>
              </div>
            </div>
          </div>
          
          <!-- Input Value Section -->
          <div *ngIf="document.inputValue" class="input-section">
            <div class="input-header">
              <mat-icon class="input-icon">text_fields</mat-icon>
              <span class="input-label">Value:</span>
            </div>
            <div class="input-display">{{document.inputValue}}</div>
          </div>
          
          <!-- Empty State -->
          <div *ngIf="document.type === 'upload' && (!document.uploadedFiles || document.uploadedFiles.length === 0)" 
               class="empty-state">
            <mat-icon class="empty-icon">cloud_upload</mat-icon>
            <span class="empty-text">No files uploaded</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Activity Log Panel -->
    <div class="activity-panel">
      <div class="panel-header">
        <div class="panel-title">
          <mat-icon class="panel-icon">history</mat-icon>
          <h2>Activity Log</h2>
          <span class="activity-count">{{activityLog.length}} entries</span>
        </div>
      </div>
      
      <div class="activity-timeline">
        <!-- Loading State -->
        <div *ngIf="isLoadingActivityLog" class="loading-container">
          <div class="loading-spinner">⏳</div>
          <p class="loading-text">Loading activity log...</p>
        </div>

        <div *ngIf="!isLoadingActivityLog && activityLog.length === 0" class="empty-activity-state">
          <mat-icon class="empty-icon">info</mat-icon>
          <p class="empty-message">No activity log found</p>
        </div>
        
        <div *ngFor="let activity of activityLog; let i = index" class="activity-entry">
          <div class="activity-dot" [ngClass]="getActivityType(activity.action)"></div>
          <div class="activity-content">
            <div class="activity-header">
              <span class="activity-action">{{activity.action}}</span>
            </div>
            <div class="activity-header">
              <span class="activity-time">{{activity.timestamp | date:'MMM dd, HH:mm'}}</span>
            </div>
            <!-- <div class="activity-details">
              <span class="activity-description">{{activity.details}}</span>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</div>